import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  Ref,
  useRef,
} from "react";
import Autosuggest from "react-autosuggest";
import "../../css/forms.css";
import { api } from "../../api/apiClient";
import { Party } from "../types/types";

/* =========================
 * Types
 * ========================= */

interface PartySearchProps {
  onPartySelected?: (party: Party | null) => void;
  searchLabel?: string;
}

/* =========================
 * Constants
 * ========================= */

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

/* =========================
 * Component
 * ========================= */

const PartySearch = forwardRef((props: PartySearchProps, ref: Ref<any>) => {
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState<Party[]>([]);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  /* =========================
   * API search
   * ========================= */

  const searchParties = async (search: string) => {
    try {
      const query = new URLSearchParams({
        search,
        page: "1",
        pageSize: PAGE_SIZE.toString(),
      }).toString();

      const data = await api<Party[]>(`/api/PartyQuery?${query}`, {
        method: "GET",
      });

      setSuggestions(data);
    } catch (error) {
      console.error("Error searching parties:", error);
    } finally {
    }
  };

  /* =========================
   * Clear search (ALWAYS resets parent)
   * ========================= */

  const clearSearch = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    setValue("");
    setSuggestions([]);

    // Reset parent state
    props.onPartySelected?.(null);
  };

  useImperativeHandle(ref, () => ({
    clearFromParent: clearSearch,
    refresh: () => {
      if (value.trim().length >= MIN_SEARCH_LENGTH) {
        searchParties(value.trim());
      }
    },
  }));

  /* =========================
   * Autosuggest handlers
   * ========================= */

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    const trimmed = value.trim();

    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchParties(trimmed);
    }, DEBOUNCE_MS);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const getSuggestionValue = (suggestion: Party) => suggestion.displayName;

  const renderSuggestion = (suggestion: Party) => (
    <div>
      {suggestion.partyType === "Organisation" ? "🏢 " : "👤 "}
      {suggestion.displayName}
      {suggestion.contactSummary && (
        <div className="summary-name">{suggestion.contactSummary}</div>
      )}
    </div>
  );

  const onChange = (_: any, { newValue }: { newValue: string }) => {
    setValue(newValue);
  };

  const onSuggestionSelected = (
    _: any,
    { suggestion }: { suggestion: Party },
  ) => {
    props.onPartySelected?.(suggestion);

    setTimeout(() => {
      setValue("");
      setSuggestions([]);
    }, 0);
  };

  /* =========================
   * Render
   * ========================= */

  return (
    <div className="search-container">
      <div className="search-input-wrapper">
        {/* Left Search Icon */}
        <div className="search-icon-left">🔍</div>

        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          inputProps={{
            placeholder: "Search name",
            "aria-label": props.searchLabel ?? "Search parties by name",
            value,
            onChange,
            className: "form-input search-input-with-icons",
          }}
          onSuggestionSelected={onSuggestionSelected}
        />

        {/* Always Visible Clear Button */}
        <button
          type="button"
          className="search-clear-button"
          onClick={clearSearch}
          aria-label="Clear search"
        >
          ✕
        </button>
      </div>
    </div>
  );
});

export default PartySearch;
